import {
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { ArcgisService } from '../../services/arcgis/arcgis.service';
import { DashboardMaps } from '../../models/dashboard-maps';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { AuthService } from '../../../auth/services/auth.service';
import { SiteToolkitComponent } from '../arcgis/site-toolkit/site-toolkit.component';
import { SafeUrlPipe } from '../../../../shared/pipes/safe-url.pipe';
import { ArcgisAuth } from '../../models/arcgis-auth';

@Component({
  selector: 'app-dashboard-arcgis-maps',
  standalone: true,
  imports: [SiteToolkitComponent, SafeUrlPipe],
  templateUrl: './dashboard-arcgis-maps.component.html',
  styleUrl: './dashboard-arcgis-maps.component.scss',
})
export class DashboardArcgisMapsComponent
  extends DestroyBase
  implements OnInit
{
  arcgisService = inject(ArcgisService);
  authService = inject(AuthService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  selectedMap: string | null = null;
  arcgisAuthInfo: ArcgisAuth | undefined;
  dashboardMaps: DashboardMaps[] = [];
  selectedIframeDashboardUrl: string | undefined;
  isLoading = false;
  isIframeLoading = false;
  isServiceDown = false;

  @ViewChild('iframeElement') iframeElement!: ElementRef;

  ngOnInit(): void {
    this.isLoading = true;
    this.initializeArcGISToken();
  }

  toggleFullscreen(): void {
    // Check if iframe element exists and is available
    if (!this.iframeElement || !this.iframeElement.nativeElement) {
      console.warn('Iframe element not available for fullscreen');
      return;
    }

    const iframe = this.iframeElement.nativeElement;

    // Check if the iframe supports fullscreen
    if (iframe.requestFullscreen) {
      iframe.requestFullscreen().catch((error: Error) => {
        console.error('Error attempting to enable fullscreen:', error);
      });
    } else if (iframe.mozRequestFullScreen) {
      // Firefox
      iframe.mozRequestFullScreen();
    } else if (iframe.webkitRequestFullscreen) {
      // Chrome, Safari, Opera
      iframe.webkitRequestFullscreen();
    } else if (iframe.msRequestFullscreen) {
      // IE/Edge
      iframe.msRequestFullscreen();
    } else {
      console.warn('Fullscreen API is not supported in this browser');
    }
  }

  refreshIframe(): void {
    if (!this.iframeElement || !this.iframeElement.nativeElement) {
      console.warn('Iframe element not available for refresh');
      return;
    }

    this.isIframeLoading = true;
    const iframe = this.iframeElement.nativeElement;
    iframe.src = this.selectedIframeDashboardUrl;
  }

  onMapSelect(map: DashboardMaps): void {
    this.selectedMap = map.id;
    this.selectedIframeDashboardUrl = map.url;
    this.isIframeLoading = true;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { map: map.slug },
      queryParamsHandling: 'merge',
    });
  }

  onIframeLoad(): void {
    this.isIframeLoading = false;
  }

  onIframeError(): void {
    this.isIframeLoading = false;
  }

  private initializeArcGISToken(): void {
    const tokenInfo = this.arcgisService.getArcgisAuthInfoFromStorage();

    if (tokenInfo) {
      this.arcgisAuthInfo = tokenInfo;
      console.log('Using existing ArcGIS token from local storage');
      // Token is available immediately, load dashboard maps
      this.loadDashboardMaps();
    } else {
      console.log('Getting new ArcGIS token via M2M authentication');
      this.arcgisService
        .getM2MToken()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (authResponse) => {
            // Check if token is null (service is down)
            if (authResponse.token === null) {
              console.warn('ArcGIS service is down - token is null');
              this.isServiceDown = true;
              this.arcgisAuthInfo = undefined;
              this.isLoading = false;
              return;
            }
            this.arcgisAuthInfo = authResponse.token;
            this.isServiceDown = false;
            console.log('Successfully obtained new ArcGIS token');
            // Token is now available, load dashboard maps
            this.loadDashboardMaps();
          },
          error: (error) => {
            console.error('Failed to obtain ArcGIS token:', error);
            this.arcgisAuthInfo = undefined;
            this.isServiceDown = true;
            this.isLoading = false;
          },
        });
    }
  }

  private loadDashboardMaps(): void {
    const dashboards = this.arcgisService.getDashboardsFromStorage();
    if (!dashboards) {
      console.warn('No dashboards configuration found in local storage');
      this.isLoading = false;
      return;
    }

    DashboardMaps.forEach((map) => {
      if (dashboards?.['siteToolkit']) {
        if (map.id === '4cc725b60d2944ffbe35d2464bee150e') {
          this.dashboardMaps.push(map);
        }
      }
      if (dashboards?.['gbra']) {
        if (map.id === '5867ce8c7c614878ad3dbf5416d75772') {
          this.dashboardMaps.push(map);
        }
      }
    });

    // Set loading to false now that dashboard maps are loaded
    this.isLoading = false;

    if (this.dashboardMaps.length === 0) {
      console.warn(
        'No accessible dashboard maps found for current user permissions',
      );
    }

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const mapSlug = params['map'];

        // If no dashboards are available, clear any selections
        if (this.dashboardMaps.length === 0) {
          this.selectedMap = null;
          this.selectedIframeDashboardUrl = undefined;
          return;
        }

        if (mapSlug && this.dashboardMaps.length > 0) {
          const selectedMapData = this.dashboardMaps.find(
            (map) => map.slug === mapSlug,
          );
          if (selectedMapData) {
            this.selectedMap = selectedMapData.id;
            this.selectedIframeDashboardUrl = selectedMapData.url;
            return;
          }
        }

        if (this.dashboardMaps.length > 0) {
          this.selectedMap = this.dashboardMaps[0].id;
          this.selectedIframeDashboardUrl = this.dashboardMaps[0].url;

          if (
            !mapSlug ||
            !this.dashboardMaps.find((map) => map.slug === mapSlug)
          ) {
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { map: this.dashboardMaps[0].slug },
              queryParamsHandling: 'merge',
            });
          }
        }
      });
  }
}
